import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import moment from "moment";
import { updatePatientReconcile } from "../../services/newdashboard";
import { toast } from "react-toastify";
import CustomSelect from "../../components/formComponent/CustomSelect";
import { useForm } from "react-hook-form";

type ReconcileApprovalModalProps = {
  open: boolean;
  onClose: () => void;
  onStart: (adminName: string) => void;
  clinicData: any;
  dateFilter: { startDate: string; endDate: string };
  initialData: any;
  onSave: () => void;
  reconcileData: any;
};

const ReconcileApprovalModal: React.FC<ReconcileApprovalModalProps> = ({
  open,
  onClose,
  onStart,
  clinicData,
  reconcileData,

  onSave,
}) => {
  const [adminName, setAdminName] = useState("");
  const [loading, setLoading] = useState(false);

  const { handleSubmit, control, reset, getValues, watch } = useForm<any>({
    defaultValues: {
      adminName: "",
      status: "",
    },
  });


  const handleStart = async () => {
    setLoading(true);
    if (!adminName.trim()) return;
    onStart(adminName);

    const data = {
      reconcile: reconcileData,
      verifiedBy: {
        name: adminName,
        date: moment().format("YYYY-MM-DD"),
      },
      status: watch("status"),
    };
    // console.log(data, "datadata")
    const response = await updatePatientReconcile(data, clinicData?.uniqueId);
    if (response?.status?.msg === "Success") {
      toast.success("Reconciliation started successfully");
      onClose();
      setAdminName("");
      onSave();
      setLoading(false);
    }
    // console.log(response);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setAdminName("");
        setLoading(false);
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: "10px" },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          fontWeight: 600,
          fontSize: "1.1rem",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        Patient Reconciliation Approval
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ pt: 2 }}>
        {/* Info Box */}
        <Box
          sx={{
            backgroundColor: "#f1f9fa",
            border: "1px solid #cfe8ea",
            borderRadius: "6px",
            p: 2,
            mb: 2,
            mt: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#0E3843", mb: 0.5 }}
          >
            Reconciliation Process
          </Typography>
          <Typography variant="body2" sx={{ color: "#555" }}>
            This will enable reconciliation mode. Use the Update buttons on
            individual patients to apply reconciliation with your approval.
          </Typography>
        </Box>

        {/* Admin Name */}
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
          Admin Name (Required): <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField
          placeholder="Enter your full name"
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <div className="pb-2">

          <CustomSelect
            name="status"
            control={control}
            label="Status"
            rules={{ required: true }}
            options={[
              { value: "select", label: "Select" },
              { value: "Opportunity Lost", label: "Opportunity Lost" },
              { value: "Removed", label: "Removed" },
              { value: "No Show", label: "No Show" },
              { value: "Cancelled", label: "Cancelled" },
              { value: "Not Booked", label: "Not Booked" },
              { value: "Approved", label: "Approved" },
              { value: "Duplicate", label: "Duplicate" },
              { value: "Guarantor", label: "Guarantor" },
              { value: "Flyer-Internal", label: "Flyer-Internal" },
            ]}
            className="w-full"
          />
        </div>

        {/* Approval Date */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: "#194e5b",
            backgroundColor: "#E5F3FC",
            p: 1,
            borderRadius: "6px",
          }}
        >
          Approval Date: {moment().format("M/D/YYYY")} (Today)
        </Typography>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: "#c9eeeb",
            color: "#0E3843",
            textTransform: "none",
            "&:hover": { backgroundColor: "#b1dfdc" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!adminName.trim() || loading}
          onClick={handleStart}
          sx={{
            backgroundColor: "#0E3843",
            textTransform: "none",
            "&:hover": { backgroundColor: "#0A2A30" },
          }}
        >
          {loading ? "Loading..." : "Start Reconciling"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReconcileApprovalModal;
